import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Zap } from 'lucide-react';
import { useRealtimePrices } from '../hooks/useRealtimePrice';
import api from '../services/api';
import eodhd from '../services/eodhd';
import type { RealTimePrice } from '../types';

// Always-present WebSocket-live symbols
const WS_SYMBOLS = [
  'BTC-USD.CC',
  'ETH-USD.CC',
  'XAUUSD.FOREX',
  'USDZAR.FOREX',
  'EURUSD.FOREX',
];

const DISPLAY_NAMES: Record<string, string> = {
  'BTC-USD.CC': 'BTC/USD',
  'ETH-USD.CC': 'ETH/USD',
  'XAUUSD.FOREX': 'GOLD',
  'USDZAR.FOREX': 'USD/ZAR',
  'EURUSD.FOREX': 'EUR/USD',
};

interface TickerItem {
  symbol: string;
  label: string;
  price: number;
  changePercent: number;
  isLive: boolean;
  decimals: number;
  prefix: string;
}

function getDecimals(symbol: string): number {
  if (symbol.endsWith('.FOREX')) return 4;
  if (symbol.includes('-USD.CC')) return 2;
  return 2;
}

function getPrefix(symbol: string): string {
  if (symbol.endsWith('.FOREX')) return '';
  if (symbol.endsWith('.JSE')) return 'R';
  return '$';
}

function formatPrice(price: number, prefix: string, decimals: number): string {
  if (price === 0) return '—';
  const formatted = price.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${prefix}${formatted}`;
}

function restChange(r: RealTimePrice): number {
  return r.change_percent ?? r.change_p ?? 0;
}

const TickerItemDisplay: React.FC<{ item: TickerItem }> = ({ item }) => {
  const isUp = item.changePercent > 0;
  const isDown = item.changePercent < 0;
  const changeColor = isUp
    ? 'text-emerald-400'
    : isDown
    ? 'text-red-400'
    : 'text-slate-400';

  return (
    <span className="inline-flex items-center gap-2 px-4 border-r border-slate-700/40 last:border-r-0 shrink-0">
      {item.isLive && (
        <span className="relative flex h-1.5 w-1.5 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
        </span>
      )}

      <span className="text-xs font-semibold text-slate-300 tracking-wide">
        {item.label}
      </span>

      <span className="text-xs font-mono text-white">
        {formatPrice(item.price, item.prefix, item.decimals)}
      </span>

      {item.changePercent !== 0 && (
        <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${changeColor}`}>
          {isUp ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
          {isUp ? '+' : ''}{item.changePercent.toFixed(2)}%
        </span>
      )}
    </span>
  );
};

const PriceTicker: React.FC = () => {
  // WebSocket live prices — used for current price only, NOT change %
  const livePrices = useRealtimePrices(WS_SYMBOLS);

  // User watchlist symbols
  const { data: watchlists = [] } = useQuery({
    queryKey: ['watchlists'],
    queryFn: () => api.getWatchlists(),
    staleTime: 5 * 60 * 1000,
  });

  const watchlistSymbols = useMemo(() => {
    const all = watchlists.flatMap(wl => wl.items?.map(i => i.symbol) ?? []);
    return [...new Set(all)].filter(s => !WS_SYMBOLS.includes(s)).slice(0, 8);
  }, [watchlists]);

  // REST prices for watchlist symbols
  const { data: restPrices = [] } = useQuery({
    queryKey: ['ticker-rest', watchlistSymbols.join(',')],
    queryFn: () => eodhd.getBulkQuotes(watchlistSymbols, true),
    staleTime: 60 * 1000,
    refetchInterval: 90 * 1000,
    enabled: watchlistSymbols.length > 0,
  });

  const items = useMemo<TickerItem[]>(() => {
    const wsItems: TickerItem[] = WS_SYMBOLS.map(sym => {
      const live = livePrices.get(sym);

      // Price: WebSocket live price; bid/ask midpoint fallback
      const wsPrice = live?.price ?? (
        live?.bid != null && live?.ask != null ? (live.bid + live.ask) / 2 : undefined
      );
      const price = wsPrice ?? 0;

      return {
        symbol: sym,
        label: DISPLAY_NAMES[sym] ?? sym.split('.')[0],
        price,
        changePercent: 0,  // WebSocket dd unreliable — suppress change% for WS symbols
        isLive: live?.isLive ?? false,
        decimals: getDecimals(sym),
        prefix: getPrefix(sym),
      };
    });

    const restItems: TickerItem[] = restPrices.map(r => {
      const sym = r.symbol ?? r.code ?? '';
      return {
        symbol: sym,
        label: sym.split('.')[0],
        price: r.close ?? 0,
        changePercent: restChange(r),
        isLive: false,
        decimals: getDecimals(sym),
        prefix: getPrefix(sym),
      };
    });

    return [...wsItems, ...restItems].filter(i => i.price > 0);
  }, [livePrices, restPrices]);

  if (items.length === 0) return null;

  const doubled = [...items, ...items];
  const duration = Math.max(20, items.length * 6);

  return (
    <>
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          animation: ticker-scroll ${duration}s linear infinite;
          will-change: transform;
        }
        .ticker-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="h-8 bg-slate-950/70 border-b border-slate-700/40 overflow-hidden flex items-center relative select-none">
        <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none" />

        <div className="absolute left-3 z-20 flex items-center gap-1 text-[10px] font-bold text-emerald-400 tracking-widest">
          <Zap size={10} className="fill-emerald-400" />
          LIVE
        </div>

        <div className="ticker-track flex items-center pl-20">
          {doubled.map((item, i) => (
            <TickerItemDisplay key={`${item.symbol}-${i}`} item={item} />
          ))}
        </div>
      </div>
    </>
  );
};

export default PriceTicker;
